import React, { useState } from 'react';
import { mapValues, pick, uniqBy } from 'lodash';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { MamEntryFields } from 'views/entry/mam/components/mamEntryOutValues';
import { useModalCtx } from 'context/ModalContext';
import {
  snrCnrFields,
  kvpValidation,
  masValidation,
  posNumValidation,
  getChartEntries,
  getModifiedEntries,
} from 'views/entry/mam/components/mamEntry.util';
import PhantomCNREditModal from 'views/entry/mam/weekly/modify/components/PhantomCNREditModal';
import EntryConfirmModal from 'views/entry/components/EntryConfirmModal';
import { EntryModes } from 'types/entry';
import ToggleableEntry from 'views/entry/components/ToggleableEntry';
import MamEntryChart from 'views/entry/mam/components/MamEntryChart';
import { RadTextInput } from 'presentational/Inputs';
import TaskNav from 'views/entry/mam/weekly/modify/components/TaskNav';
import SnrCnrEntryForm from './SnrCnrEntryForm';
import SnrCnrEntryDisplay from './SnrCnrEntryDisplay';

const useStyles = makeStyles({
  tableTitles: {
    color: 'black',
    padding: '0',
    textAlign: 'center',
    width: '30px',
    height: '50px',
  },
  remarks: {
    width: '100%',
    overflow: 'auto',
    border: '1px solid #80808026',
  },
  tableContainer: {
    display: 'flex',
    margin: '0 2rem',
    '@media (max-width: 1024px)': {
      margin: '0',
    },
  },
  stickyHeader: {
    width: '5rem',
  },
  rightTables: {
    maxWidth: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'row',
  },
  remarkSection: {
    margin: '2rem 2rem 0 2rem',
  },
  remarkTitle: {
    marginBottom: '1rem',
  },
  chartBoxes: {
    marginTop: '2rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: '90%',
  },
  chartSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  link: {
    textDecoration: 'none',
  },
  editCNRSection: {
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2rem',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  currCNR: {
    padding: '1rem',
  },
});

const taskName = 'SNR & CNR';
const snrChartOpts = { suggestedMin: 30, suggestedMax: 70 };
const cnrChartOpts = { suggestedMin: -20, suggestedMax: 20 };
const newField = 'cnrPercentDiff';
const fieldTitles = {
  [MamEntryFields.snr]: 'SNR',
  [MamEntryFields.cnr]: 'CNR',
};

const modifyCnr = (entry) => ((entry.cnr - entry.cnrBaseline) / entry.cnrBaseline) * 100;

function SnrCnrDashboard(props) {
  const { activeEntry, mode, saveEntries, mamEntries, taskId, formDisabled, match, device } = props;
  const classes = useStyles();
  const [entryKey, setEntryKey] = useState(0);
  const { showModal } = useModalCtx();

  const initialValues = { entries: {} };
  let enterModeColumns = null;
  if (mode === EntryModes.ENTER) {
    initialValues.entries = { new: pick(activeEntry, [...snrCnrFields, 'cnrBaseline', 'snrCnrRemarks']) };
    enterModeColumns = (
      <>
        {mamEntries.leading.map((entry) => (
          <SnrCnrEntryDisplay key={entry._id} entryData={entry} />
        ))}
        <SnrCnrEntryForm entryData={activeEntry} formDisabled={formDisabled} />
        {mamEntries.trailing.map((entry) => (
          <SnrCnrEntryDisplay key={entry._id} entryData={entry} />
        ))}
      </>
    );
  }

  const editCNRBase = () => {
    showModal(PhantomCNREditModal, {
      match,
      editType: 'cnrBaseline',
      label: 'CNR Baseline',
      message: 'Here you can edit CNR Baseline.',
      actionText: 'Confirm',
    });
  };

  const handleSubmit = (values, submitForm) => {
    const newEntries = Object.keys(values.entries).map((entry) => values.entries[entry]);
    if (mode === EntryModes.ENTER) {
      newEntries[0].date = activeEntry.date;
      newEntries[0].initials = activeEntry.initials;
    }
    showModal(EntryConfirmModal, {
      entryType: 'mam',
      confirmTitle: 'Mammograph SNR CNR',
      confirmEntries: newEntries,
      accept: () => submitForm(),
      fieldTitles,
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        entries: Yup.lazy((obj) =>
          Yup.object(
            mapValues(obj, () =>
              Yup.object({
                cnrKvp: kvpValidation,
                cnrMas: masValidation,
                cnrEl: posNumValidation,
                snr: Yup.number().min(30.0).max(70.0).required('Required'),
                cnr: posNumValidation,
                cnrBaseline: posNumValidation,
                snrCnrRemarks: Yup.string(),
                outFields: Yup.array(),
              }),
            ),
          ),
        ),
      })}
      onSubmit={async (values) => {
        const newEntries = Object.keys(values.entries).map((entry) => values.entries[entry]);
        if (mode === EntryModes.ENTER) {
          newEntries[0].snrCnrPerformed = true;
        }
        saveEntries(newEntries);
        // The following is used to force React to re-render the form
        setEntryKey(entryKey + 1);
      }}
    >
      {({ values, submitForm }) => {
        let allEntries = getChartEntries(mamEntries, activeEntry.date, values.entries);
        if (mode === EntryModes.EDIT) {
          allEntries = [
            ...mamEntries.leading,
            ...Object.values(values.entries),
            ...mamEntries.trailing,
            /* eslint-disable-next-line no-nested-ternary */
          ].sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0));
          allEntries = uniqBy(allEntries, '_id');
        }
        return (
          <Form>
            <Box className={classes.tableContainer}>
              <Table stickyHeader className={classes.stickyHeader}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>Date</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>Initials</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>kVp</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>mAs</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>El</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>SNR</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>CNR</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.tableTitles}>Remarks</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} className={classes.tableTitles} />
                  </TableRow>
                </TableHead>
              </Table>

              <Box className={classes.rightTables}>
                <Table>
                  <TableBody>
                    <TableRow>
                      {enterModeColumns ||
                        allEntries.map((entry) => (
                          <ToggleableEntry
                            key={entryKey + entry.date}
                            entryData={entry}
                            mode={mode}
                            entryDisplay={SnrCnrEntryDisplay}
                            entryForm={SnrCnrEntryForm}
                          />
                        ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>

            <Box className={classes.chartSection}>
              <Box className={classes.chartBoxes}>
                <MamEntryChart
                  entries={allEntries}
                  field={MamEntryFields.snr}
                  title={fieldTitles[MamEntryFields.snr]}
                  chartOpts={snrChartOpts}
                />
              </Box>

              <Box className={classes.editCNRSection}>
                <Box className={classes.currCNR}>
                  <b>Current CNR Baseline:</b> {device?.cnrBaseline}
                </Box>
                <Button variant="outlined" color="primary" onClick={() => editCNRBase()}>
                  Edit CNR Baseline
                </Button>
              </Box>

              <Box className={classes.chartBoxes}>
                <MamEntryChart
                  entries={getModifiedEntries(allEntries, MamEntryFields.cnr, newField, modifyCnr)}
                  field={newField}
                  title="CNR % Difference from Baseline"
                  chartOpts={cnrChartOpts}
                />
              </Box>
            </Box>

            {mode === EntryModes.ENTER && (
              <Box className={classes.remarkSection}>
                <Typography className={classes.remarkTitle} fontSize={16}>
                  Remarks
                </Typography>
                <RadTextInput
                  multiline
                  disabled={formDisabled}
                  name="entries.new.snrCnrRemarks"
                  type="text"
                  classes={{ root: classes.remarks }}
                />
              </Box>
            )}

            <TaskNav
              taskName={taskName}
              taskId={taskId}
              onComplete={() => handleSubmit(values, submitForm)}
              mode={mode}
            />
          </Form>
        );
      }}
    </Formik>
  );
}

SnrCnrDashboard.propTypes = {
  activeEntry: PropTypes.objectOf(PropTypes.any).isRequired,
  mode: PropTypes.string,
  saveEntries: PropTypes.func.isRequired,
  mamEntries: PropTypes.objectOf(PropTypes.any),
  taskId: PropTypes.number.isRequired,
  formDisabled: PropTypes.bool,
};

SnrCnrDashboard.defaultProps = {
  mode: EntryModes.ENTER,
  mamEntries: { leading: [], trailing: [] },
  formDisabled: false,
};

export default SnrCnrDashboard;
